/**
 * Business capability and lending enums for B2B partner relationships
 * @see Issue #238 - Partner Companies B2B Model
 */

/**
 * Loan-product capabilities a partner (B2B) advertises in its referral
 * agreements — i.e. the kinds of loans the partner can originate, broker, or
 * exclude. This is a flat capability tag for partner discovery, NOT the
 * runtime per-business product identity (which is the `LoanProduct` model in
 * 29-case-management-service, see Issue #1388).
 *
 * @see Issue #238 — Partner Companies B2B Model (introduced this enum).
 * @see Issue #1388 — Renamed from `LoanProductType` to disambiguate from the
 *   new `LoanProduct` runtime model. The legacy `LoanProductType` export is
 *   preserved as a `@deprecated` alias below; new code should reference
 *   `PartnerLoanCapability` directly.
 */
export enum PartnerLoanCapability {
  FIX_FLIP = 'fix_flip',
  BRIDGE = 'bridge',
  CONSTRUCTION = 'construction',
  DSCR = 'dscr', // Debt Service Coverage Ratio loans
  RENTAL = 'rental',
  GROUND_UP = 'ground_up',
  COMMERCIAL = 'commercial',
  MIXED_USE = 'mixed_use',
}

/**
 * @deprecated Issue #1388 — Renamed to `PartnerLoanCapability`; preserved for
 * transition-window compatibility. New code should import `PartnerLoanCapability`.
 */
export const LoanProductType = PartnerLoanCapability;
export type LoanProductType = PartnerLoanCapability;

/**
 * US State codes for territory/licensing
 */
export enum USStateCode {
  AL = 'AL',
  AK = 'AK',
  AZ = 'AZ',
  AR = 'AR',
  CA = 'CA',
  CO = 'CO',
  CT = 'CT',
  DE = 'DE',
  FL = 'FL',
  GA = 'GA',
  HI = 'HI',
  ID = 'ID',
  IL = 'IL',
  IN = 'IN',
  IA = 'IA',
  KS = 'KS',
  KY = 'KY',
  LA = 'LA',
  ME = 'ME',
  MD = 'MD',
  MA = 'MA',
  MI = 'MI',
  MN = 'MN',
  MS = 'MS',
  MO = 'MO',
  MT = 'MT',
  NE = 'NE',
  NV = 'NV',
  NH = 'NH',
  NJ = 'NJ',
  NM = 'NM',
  NY = 'NY',
  NC = 'NC',
  ND = 'ND',
  OH = 'OH',
  OK = 'OK',
  OR = 'OR',
  PA = 'PA',
  RI = 'RI',
  SC = 'SC',
  SD = 'SD',
  TN = 'TN',
  TX = 'TX',
  UT = 'UT',
  VT = 'VT',
  VA = 'VA',
  WA = 'WA',
  WV = 'WV',
  WI = 'WI',
  WY = 'WY',
  DC = 'DC',
}

/**
 * Array of all US state codes for iteration
 */
export const US_STATE_CODES = Object.values(USStateCode);

/**
 * Human-readable state names
 */
export const US_STATE_NAMES: Record<USStateCode, string> = {
  [USStateCode.AL]: 'Alabama',
  [USStateCode.AK]: 'Alaska',
  [USStateCode.AZ]: 'Arizona',
  [USStateCode.AR]: 'Arkansas',
  [USStateCode.CA]: 'California',
  [USStateCode.CO]: 'Colorado',
  [USStateCode.CT]: 'Connecticut',
  [USStateCode.DE]: 'Delaware',
  [USStateCode.FL]: 'Florida',
  [USStateCode.GA]: 'Georgia',
  [USStateCode.HI]: 'Hawaii',
  [USStateCode.ID]: 'Idaho',
  [USStateCode.IL]: 'Illinois',
  [USStateCode.IN]: 'Indiana',
  [USStateCode.IA]: 'Iowa',
  [USStateCode.KS]: 'Kansas',
  [USStateCode.KY]: 'Kentucky',
  [USStateCode.LA]: 'Louisiana',
  [USStateCode.ME]: 'Maine',
  [USStateCode.MD]: 'Maryland',
  [USStateCode.MA]: 'Massachusetts',
  [USStateCode.MI]: 'Michigan',
  [USStateCode.MN]: 'Minnesota',
  [USStateCode.MS]: 'Mississippi',
  [USStateCode.MO]: 'Missouri',
  [USStateCode.MT]: 'Montana',
  [USStateCode.NE]: 'Nebraska',
  [USStateCode.NV]: 'Nevada',
  [USStateCode.NH]: 'New Hampshire',
  [USStateCode.NJ]: 'New Jersey',
  [USStateCode.NM]: 'New Mexico',
  [USStateCode.NY]: 'New York',
  [USStateCode.NC]: 'North Carolina',
  [USStateCode.ND]: 'North Dakota',
  [USStateCode.OH]: 'Ohio',
  [USStateCode.OK]: 'Oklahoma',
  [USStateCode.OR]: 'Oregon',
  [USStateCode.PA]: 'Pennsylvania',
  [USStateCode.RI]: 'Rhode Island',
  [USStateCode.SC]: 'South Carolina',
  [USStateCode.SD]: 'South Dakota',
  [USStateCode.TN]: 'Tennessee',
  [USStateCode.TX]: 'Texas',
  [USStateCode.UT]: 'Utah',
  [USStateCode.VT]: 'Vermont',
  [USStateCode.VA]: 'Virginia',
  [USStateCode.WA]: 'Washington',
  [USStateCode.WV]: 'West Virginia',
  [USStateCode.WI]: 'Wisconsin',
  [USStateCode.WY]: 'Wyoming',
  [USStateCode.DC]: 'District of Columbia',
};

/**
 * Display names for `PartnerLoanCapability` values — used in partner-discovery
 * UI (Issue #238).
 */
export const PARTNER_LOAN_CAPABILITY_NAMES: Record<PartnerLoanCapability, string> = {
  [PartnerLoanCapability.FIX_FLIP]: 'Fix & Flip',
  [PartnerLoanCapability.BRIDGE]: 'Bridge',
  [PartnerLoanCapability.CONSTRUCTION]: 'Construction',
  [PartnerLoanCapability.DSCR]: 'DSCR',
  [PartnerLoanCapability.RENTAL]: 'Rental',
  [PartnerLoanCapability.GROUND_UP]: 'Ground Up',
  [PartnerLoanCapability.COMMERCIAL]: 'Commercial',
  [PartnerLoanCapability.MIXED_USE]: 'Mixed Use',
};

/**
 * @deprecated Issue #1388 — Renamed to `PARTNER_LOAN_CAPABILITY_NAMES`.
 * The alias is preserved so existing imports keep working through the
 * transition window.
 */
export const LOAN_PRODUCT_TYPE_NAMES = PARTNER_LOAN_CAPABILITY_NAMES;
